import { useEffect, useRef, useState } from "react";
import { useShallow } from "zustand/shallow";
import { useLocation, useNavigate, useNavigationType } from "react-router";
import { Button, Dialog, DialogActions, DialogContent, Typography } from "@mui/material";
import Picker from "@emoji-mart/react";

import SlideUpTransition from "components/SlideUpTransition";
import useBoundStore from "store";
import styles from "./emojiPicker.module.scss";

export const DEFAULT_EMOJI: Emoji = {
  id: "grey_question",
  name: "White Question Mark",
  native: "❔",
  unified: "2754",
  keywords: ["grey", "doubts", "gray", "huh", "confused"],
  shortcodes: ":grey_question:",
};

export interface EmojiPickerProps {
  value?: Emoji;
  onChange: (emoji: Emoji) => void;
}

export default function EmojiPicker(props: EmojiPickerProps): JSX.Element {
  const { value, onChange } = props;
  const [emojiPreview, setEmojiPreview] = useState<Emoji>();
  const [open, setOpen] = useState(false);
  const { isMobileDevice, theme } = useBoundStore(
    useShallow((state) => ({
      isMobileDevice: state.miscellaneous.isMobileDevice,
      theme: state.generalSettings.theme,
    }))
  );
  const selectedEmoji = useRef<Emoji>();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const navigationType = useNavigationType();

  function handleOpenEmojiPicker() {
    navigate(routerLocation.pathname);
    setOpen(true);
  }

  function handleConfirmEmoji() {
    if (selectedEmoji.current) {
      onChange(selectedEmoji.current);
    }
    navigate(-1);
  }

  function handleEmojiSelect(emoji: Emoji) {
    setEmojiPreview(emoji);
    selectedEmoji.current = emoji;
  }

  function handleCancel() {
    navigate(-1);
  }

  // Set default emoji when emoji picker is opened
  useEffect(() => {
    if (open) {
      setEmojiPreview(value ?? DEFAULT_EMOJI);
    }
  }, [open, value]);

  /**
   * On pressing back button close opened dialogs
   */
  useEffect(() => {
    if (navigationType === "POP") {
      setOpen(false);
    }
  }, [navigationType]);

  return (
    <div className={styles.emojiPicker}>
      <Button
        aria-label={value?.name ?? DEFAULT_EMOJI.name}
        fullWidth
        size='small'
        variant='lowercaseSimple'
        onClick={handleOpenEmojiPicker}
      >
        <EmEmoji id={value?.id ?? DEFAULT_EMOJI.id} size={24} skin={value?.skin} />
      </Button>

      <Dialog
        aria-labelledby='Emoji picker'
        open={open}
        fullScreen={isMobileDevice}
        fullWidth
        maxWidth='xs'
        TransitionComponent={isMobileDevice ? SlideUpTransition : undefined}
      >
        <DialogContent className={styles.dialogContent}>
          <Picker dynamicWidth previewPosition='none' theme={theme} onEmojiSelect={handleEmojiSelect} />
          <div className={styles.emojiPreview}>
            <span>
              <EmEmoji id={emojiPreview?.id ?? DEFAULT_EMOJI.id} size={32} skin={emojiPreview?.skin} />
            </span>
            <Typography marginLeft='1rem'> {emojiPreview?.name}</Typography>
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button onClick={handleConfirmEmoji}>Ok</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

interface EmojiProps {
  id: string;
  /** In pixels */
  size?: number;
  skin?: number;
}

export function EmEmoji(props: EmojiProps): JSX.Element {
  const { id, size, skin } = props;

  /* @ts-ignore next line */
  return <em-emoji id={id} skin={skin} style={{ fontSize: size }} />;
}

export type EmojiSkin = 1 | 2 | 3 | 4 | 5 | 6;

export interface Emoji {
  aliases?: string[];
  emoticons?: string[];
  id: string;
  keywords: string[];
  name: string;
  native: string;
  shortcodes: string;
  skin?: EmojiSkin;
  unified: string;
}
